import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import API from '../api/api';
import logo from '../assets/logo.png';
import ProfileDropdown from '../components/ProfileDropdown';
import PrettySelect from '../components/PrettySelect';
import Toast from '../components/Toast';
import { getStoredUserForRole } from '../utils/authStorage';
import '../styles/newrecruiter.css';
import '../styles/candidates-page.css';
import '../styles/exam-management.css';
import '../styles/drives.css';

const IconHome = ({ active }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 14V8.7A.7.7 0 0 0 9.3 8H6.7a.7.7 0 0 0-.7.7V14" stroke={active ? 'white' : '#26282B'} strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round"/><path d="M2 6.7 8 1.6l6 5.1V13a1.3 1.3 0 0 1-1.3 1.3H3.3A1.3 1.3 0 0 1 2 13V6.7Z" stroke={active ? 'white' : '#26282B'} strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round"/></svg>
);

const IconDrive = ({ active }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M3.3 6.7h13.4M5 3.3h10a1.7 1.7 0 0 1 1.7 1.7v10A1.7 1.7 0 0 1 15 16.7H5A1.7 1.7 0 0 1 3.3 15V5A1.7 1.7 0 0 1 5 3.3Z" stroke={active ? 'white' : '#26282B'} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/><path d="M7 10h.01M10 10h3M7 13h.01M10 13h3" stroke={active ? 'white' : '#26282B'} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
);

const IconExamManagement = ({ active }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M12.5 1.7h-5a.8.8 0 0 0-.8.8v1.7c0 .4.3.8.8.8h5c.4 0 .8-.3.8-.8V2.5c0-.4-.3-.8-.8-.8Z" stroke={active ? 'white' : '#26282B'} strokeWidth="1.65" strokeLinecap="round" strokeLinejoin="round"/><path d="M13.3 3.3H15c.9 0 1.7.8 1.7 1.7v11.7c0 .9-.8 1.7-1.7 1.7H5c-.9 0-1.7-.8-1.7-1.7V5c0-.9.8-1.7 1.7-1.7h1.7M10 9.2h3.3M10 13.3h3.3M6.7 9.2h.01M6.7 13.3h.01" stroke={active ? 'white' : '#26282B'} strokeWidth="1.65" strokeLinecap="round" strokeLinejoin="round"/></svg>
);

const IconCandidates = ({ active }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke={active ? 'white' : '#26282B'} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/><circle cx="9" cy="7" r="4" stroke={active ? 'white' : '#26282B'} strokeWidth="1.7"/><path d="M23 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8" stroke={active ? 'white' : '#26282B'} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>
);

const IconSections = ({ active }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M2.5 5h15M2.5 10h15M2.5 15h15" stroke={active ? 'white' : '#26282B'} strokeWidth="1.7" strokeLinecap="round"/></svg>
);

const IconQuestionBank = ({ active }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 14.2h.01M12.5 1.7H5c-.9 0-1.7.7-1.7 1.6v13.4c0 .9.8 1.6 1.7 1.6h10c.9 0 1.7-.7 1.7-1.6V5.8l-4.2-4.1Z" stroke={active ? 'white' : '#26282B'} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/><path d="M7.6 7.5a2.5 2.5 0 1 1 2.4 3.3" stroke={active ? 'white' : '#26282B'} strokeWidth="1.7" strokeLinecap="round"/></svg>
);

const IconAnalytics = ({ active }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M2.5 2.5v13.3c0 .9.8 1.7 1.7 1.7h13.3M15 14.2V7.5M10.8 14.2v-10M6.7 14.2v-2.5" stroke={active ? 'white' : '#26282B'} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>
);

const sidebarItems = [
  { label: 'Home', Icon: IconHome, path: '/recruiter' },
  { label: 'Drives', Icon: IconDrive, path: '/drives' },
  { label: 'Exam Management', Icon: IconExamManagement, path: '/exam-management' },
  { label: 'Candidates', Icon: IconCandidates, path: '/candidates' },
  { label: 'Sections', Icon: IconSections, path: '/sections' },
  { label: 'Question Bank', Icon: IconQuestionBank, path: '/question-bank' },
  { label: 'Analytics', Icon: IconAnalytics, path: '/analytics' },
];

const emptyDriveForm = { name: '', date: '', startTime: '', endTime: '', venue: '', examId: '', candidateIds: [] };
const todayInputValue = () => new Date().toISOString().slice(0, 10);

const getInitials = (name = '') => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'C';
  return parts.slice(0, 2).map(part => part[0]?.toUpperCase()).join('');
};
const TIME_OPTIONS = Array.from({ length: 48 }, (_, index) => {
  const hours = String(Math.floor(index / 2)).padStart(2, '0');
  const minutes = index % 2 === 0 ? '00' : '30';
  return `${hours}:${minutes}`;
});
const DRIVE_CANDIDATE_STATUS_OPTIONS = [
  'All Status',
  'Added',
  'Absent',
  'Assigned',
  'Ongoing',
  'Completed',
  'Defaulter',
  'Shortlisted',
  'Not Shortlisted',
];

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatTime = (value) => {
  if (!value) return '-';
  const [hours, minutes] = String(value).split(':');
  if (!hours || !minutes) return value;
  const date = new Date();
  date.setHours(Number(hours), Number(minutes), 0, 0);
  return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};

const formatTimeRange = (start, end) => {
  const startText = formatTime(start);
  const endText = formatTime(end);
  if (startText === '-' && endText === '-') return '-';
  return `${startText} - ${endText}`;
};

const toDateInputValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const toTimeInputValue = (value) => {
  if (!value) return '';
  const [hours = '', minutes = ''] = String(value).split(':');
  return hours && minutes ? `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}` : '';
};

const driveToForm = (drive) => ({
  name: drive?.name || '',
  date: toDateInputValue(drive?.drive_date),
  startTime: toTimeInputValue(drive?.drive_time),
  endTime: toTimeInputValue(drive?.end_time),
  venue: drive?.venue || '',
  examId: drive?.exam_id || '',
  candidateIds: [],
});

const IconEyeAction = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
    <path d="M2.5 10s2.7-5 7.5-5 7.5 5 7.5 5-2.7 5-7.5 5-7.5-5-7.5-5Z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="10" cy="10" r="2.3" stroke="currentColor" strokeWidth="1.7"/>
  </svg>
);

const IconEditAction = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
    <path d="M11.7 4.1 15.9 8.3M3.3 16.7l4.4-.9 8.7-8.7a2 2 0 0 0-2.8-2.8L4.9 13l-1.6 3.7Z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconPlusAction = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
    <path d="M10 4.2v11.6M4.2 10h11.6" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"/>
  </svg>
);

const IconDeleteAction = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
    <path d="M3.3 5h13.4M8.3 8.3v5M11.7 8.3v5M5.8 5l.8 11.2c.1.8.8 1.4 1.6 1.4h3.6c.8 0 1.5-.6 1.6-1.4L14.2 5M7.5 5V3.3c0-.5.4-.8.8-.8h3.4c.5 0 .8.4.8.8V5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

function DriveTimeSelect({ label, value, onChange, minTime }) {
  const [open, setOpen] = useState(false);
  const selectedLabel = value || '--:--';

  return (
    <label className="drive-field drive-time-field">
      <span>{label}</span>
      <button type="button" className="drive-time-trigger" onClick={() => setOpen(prev => !prev)}>
        <span>{selectedLabel}</span>
        <svg width="15" height="15" viewBox="0 0 20 20" fill="none">
          <circle cx="10" cy="10" r="7" stroke="#111827" strokeWidth="1.5"/>
          <path d="M10 6.5V10L12.5 11.5" stroke="#111827" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {open && (
        <div className="drive-time-menu">
          {TIME_OPTIONS.map(option => {
            const disabled = Boolean(minTime && option < minTime);
            return (
            <button
              type="button"
              key={option}
              className={option === value ? 'is-selected' : ''}
              disabled={disabled}
              onClick={() => {
                if (disabled) return;
                onChange(option);
                setOpen(false);
              }}
            >
              {option}
            </button>
          );
          })}
        </div>
      )}
    </label>
  );
}

function AddCandidateChoiceModal({ onClose, onSingleEntry, onBulkUpload }) {
  return (
    <div className="cp-modal-overlay drive-candidate-choice-overlay" onClick={onClose}>
      <div className="cp-modal" onClick={event => event.stopPropagation()}>
        <button className="cp-modal-close" onClick={onClose}>
          <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
            <path d="M10 1L1 10" stroke="#94A3B8" strokeWidth="1.84" strokeLinecap="round"/>
            <path d="M1 1L10 10" stroke="#94A3B8" strokeWidth="1.84" strokeLinecap="round"/>
          </svg>
        </button>
        <h2 className="cp-modal-title">Assign Candidates</h2>
        <p className="cp-modal-subtitle">Choose how you want to assign this drive to candidates</p>
        <div className="cp-modal-actions">
          <button className="cp-modal-btn" onClick={onSingleEntry}>
            <span className="cp-modal-btn-icon">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M9 10.5V9.5C9 8.97 8.789 8.462 8.414 8.086C8.038 7.711 7.53 7.5 7 7.5H4C3.47 7.5 2.962 7.711 2.586 8.086C2.211 8.462 2 8.97 2 9.5V10.5" stroke="#4683E5" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M5.5 5.5C6.605 5.5 7.5 4.605 7.5 3.5C7.5 2.395 6.605 1.5 5.5 1.5C4.395 1.5 3.5 2.395 3.5 3.5C3.5 4.605 4.395 5.5 5.5 5.5Z" stroke="#4683E5" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9.5 4V7" stroke="#4683E5" strokeWidth="1" strokeLinecap="round"/>
                <path d="M8 5.5H11" stroke="#4683E5" strokeWidth="1" strokeLinecap="round"/>
              </svg>
            </span>
            Choose Candidates
          </button>
          <button className="cp-modal-btn" onClick={onBulkUpload}>
            <span className="cp-modal-btn-icon">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M10 7.5V9.5C10 9.765 9.895 10.02 9.707 10.207C9.52 10.395 9.265 10.5 9 10.5H3C2.735 10.5 2.48 10.395 2.293 10.207C2.105 10.02 2 9.765 2 9.5V7.5" stroke="#4683E5" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M8.5 4L6 1.5L3.5 4" stroke="#4683E5" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M6 1.5V7.5" stroke="#4683E5" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
            Bulk Upload
          </button>
        </div>
      </div>
    </div>
  );
}

// eslint-disable-next-line no-unused-vars
function DriveSingleEntryForm({ onClose, onCreated, onNotify }) {
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', mobileNo: '', skill: '', collegeName: '', degree: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const { firstName, lastName, email, mobileNo, skill, collegeName, degree } = formData;
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !mobileNo.trim() || !skill.trim()) return;
    if (!/^\d{10}$/.test(mobileNo.trim())) {
      onNotify?.({ message: 'Mobile number must be exactly 10 digits with no letters or special characters', type: 'error' });
      return;
    }
    setLoading(true);
    try {
      const res = await API.post('/users/candidates', {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        mobileNo: mobileNo.trim(),
        company: 'inteliment',
        skill: skill.trim(),
        collegeName: collegeName.trim(),
        degree: degree.trim(),
      });
      const created = res.data?.user;
      if (created?.id) onCreated?.([created]);
      onNotify?.({ message: 'Candidate created successfully.', type: 'success' });
      onClose();
    } catch (err) {
      onNotify?.({ message: err.response?.data?.message || 'Failed to create candidate.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cp-modal-overlay drive-candidate-choice-overlay" onClick={onClose}>
      <div className="cp-modal cp-modal-form" onClick={event => event.stopPropagation()}>
        <button className="cp-modal-close" onClick={onClose}>
          <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
            <path d="M10 1L1 10" stroke="#94A3B8" strokeWidth="1.84" strokeLinecap="round"/>
            <path d="M1 1L10 10" stroke="#94A3B8" strokeWidth="1.84" strokeLinecap="round"/>
          </svg>
        </button>
        <h2 className="cp-modal-title" style={{ alignSelf: 'flex-start' }}>Add Candidate</h2>
        <p className="cp-modal-subtitle" style={{ alignSelf: 'flex-start', textAlign: 'left', maxWidth: 'none' }}>Enter Candidate Details To Get Started.</p>
        <form className="cp-form-grid" onSubmit={handleSubmit}>
          <div className="cp-form-group" style={{ gridColumn: 1, gridRow: 1 }}>
            <label>First Name<span className="required-star">*</span></label>
            <input type="text" placeholder="Enter first name" value={formData.firstName} onChange={event => setFormData(prev => ({ ...prev, firstName: event.target.value }))} required />
          </div>
          <div className="cp-form-group" style={{ gridColumn: 2, gridRow: 1 }}>
            <label>Last Name<span className="required-star">*</span></label>
            <input type="text" placeholder="Enter last name" value={formData.lastName} onChange={event => setFormData(prev => ({ ...prev, lastName: event.target.value }))} required />
          </div>
          <div className="cp-form-group" style={{ gridColumn: 1, gridRow: 2 }}>
            <label>Email<span className="required-star">*</span></label>
            <input type="email" placeholder="Enter email" value={formData.email} onChange={event => setFormData(prev => ({ ...prev, email: event.target.value }))} required />
          </div>
          <div className="cp-form-group cp-skill-group" style={{ gridColumn: 2, gridRow: 2 }}>
            <label>Primary Skill<span className="required-star">*</span></label>
            <input type="text" className="cp-form-input-text" placeholder="e.g. Frontend, Backend, DevOps..." value={formData.skill} onChange={event => setFormData(prev => ({ ...prev, skill: event.target.value }))} required />
          </div>
          <div className="cp-form-group" style={{ gridColumn: 1, gridRow: 3 }}>
            <label>Mobile No.<span className="required-star">*</span></label>
            <input type="tel" placeholder="Enter mobile number" value={formData.mobileNo} onChange={event => setFormData(prev => ({ ...prev, mobileNo: event.target.value }))} required />
          </div>
          <div className="cp-form-group" style={{ gridColumn: 2, gridRow: 3 }}>
            <label>College Name</label>
            <input type="text" placeholder="Enter college name" value={formData.collegeName} onChange={event => setFormData(prev => ({ ...prev, collegeName: event.target.value }))} />
          </div>
          <div className="cp-form-group" style={{ gridColumn: 1, gridRow: 4 }}>
            <label>Degree</label>
            <input type="text" placeholder="Enter degree" value={formData.degree} onChange={event => setFormData(prev => ({ ...prev, degree: event.target.value }))} />
          </div>
          <button type="submit" className="cp-add-btn cp-form-submit" disabled={loading} style={{ alignSelf: 'flex-end', marginTop: '20px', gridColumn: '1 / span 2', justifySelf: 'end' }}>
            {loading ? 'Adding...' : 'Add Candidate'}
          </button>
        </form>
      </div>
    </div>
  );
}

function DriveBulkUploadForm({ onClose, onCreated, onNotify }) {
  const [dragging, setDragging] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const downloadTemplate = () => {
    const csv = 'first_name,last_name,email,mobile_no,college,degree,primary_skill\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'candidates-template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const text = await file.text();
      const lines = text.trim().split('\n').filter(Boolean);
      const headers = (lines[0] || '').split(',').map(header => header.trim().replace(/^\uFEFF/, ''));
      const candidates = lines.slice(1).map(line => {
        const values = line.split(',').map(value => value.trim());
        const row = {};
        headers.forEach((header, index) => { row[header] = values[index] || ''; });
        return row;
      }).filter(row => row.first_name && row.last_name && row.email);
      const res = await API.post('/users/candidates/bulk', { candidates });
      const created = Array.isArray(res.data?.created) ? res.data.created : [];
      if (created.length) onCreated?.(created);
      onNotify?.({ message: `${created.length} candidates created successfully.`, type: 'success' });
      onClose();
    } catch (err) {
      onNotify?.({ message: err.response?.data?.message || 'Error processing file.', type: 'error' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="cp-modal-overlay drive-candidate-choice-overlay" onClick={onClose}>
      <div className="cp-bulk-modal" onClick={event => event.stopPropagation()}>
        <button className="cp-modal-close" onClick={onClose}>
          <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
            <path d="M10 1L1 10" stroke="#94A3B8" strokeWidth="1.84" strokeLinecap="round"/>
            <path d="M1 1L10 10" stroke="#94A3B8" strokeWidth="1.84" strokeLinecap="round"/>
          </svg>
        </button>
        <div className="cp-bulk-header">
          <h2 className="cp-bulk-title">Bulk Upload Candidates</h2>
          <p className="cp-bulk-subtitle">Upload a CSV file with columns: first_name, last_name, email, mobile_no, college, degree, primary_skill</p>
        </div>
        <button className="cp-bulk-download-link" onClick={downloadTemplate}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M10 13V5M10 13L7 10M10 13L13 10" stroke="#3B82F6" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M4 14V15.5C4 16.328 4.672 17 5.5 17H14.5C15.328 17 16 16.328 16 15.5V14" stroke="#3B82F6" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Download Template</span>
        </button>
        <div
          className={`cp-bulk-dropzone${dragging ? ' cp-bulk-dropzone--active' : ''}`}
          onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            if (event.dataTransfer.files[0]) setFile(event.dataTransfer.files[0]);
          }}
        >
          <div className="cp-bulk-upload-icon">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
              <rect width="48" height="48" rx="24" fill="#EFF6FF"/>
              <path d="M24 30V18M24 18L19 23M24 18L29 23" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 30V31.5C16 32.328 16.672 33 17.5 33H30.5C31.328 33 32 32.328 32 31.5V30" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          {file ? (
            <div className="cp-bulk-file-selected">
              <p className="cp-bulk-drop-text" style={{ color: '#155DFC', fontWeight: 500 }}>{file.name}</p>
              <button type="button" className="cp-bulk-file-clear" onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}>Clear File</button>
            </div>
          ) : (
            <p className="cp-bulk-drop-text">Drag &amp; drop your CSV file here</p>
          )}
          <label className="cp-bulk-browse">
            or <span>browse files</span>
            <input ref={fileInputRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={event => { if (event.target.files[0]) setFile(event.target.files[0]); }} />
          </label>
        </div>
        <div className="cp-bulk-guidelines">
          <span>Accepted format: CSV</span>
          <span>Columns: first_name, last_name, email, mobile_no, college, degree, primary_skill</span>
        </div>
        <button className={`cp-bulk-upload-btn${file ? ' cp-bulk-upload-btn--active' : ''}`} disabled={!file || uploading} onClick={handleUpload}>
          {uploading ? 'Uploading...' : 'Upload & Create Candidates'}
        </button>
      </div>
    </div>
  );
}

function DriveChooseCandidatesModal({ onClose, onAssign }) {
  const [candidates, setCandidates] = useState([]);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [newCandidate, setNewCandidate] = useState({ firstName: '', lastName: '', email: '', mobileNo: '', skill: '' });
  const [inlineError, setInlineError] = useState('');

  useEffect(() => {
    API.get('/users/candidates')
      .then(res => setCandidates(Array.isArray(res.data?.candidates) ? res.data.candidates : []))
      .catch(() => setCandidates([]));
  }, []);

  const filtered = candidates.filter(candidate => {
    const text = `${candidate.name || ''} ${candidate.email || ''} ${candidate.mobile_no || ''}`.toLowerCase();
    return text.includes(query.toLowerCase());
  });
  const manualFormReady = ['firstName', 'lastName', 'email', 'mobileNo']
    .every(key => String(newCandidate[key] || '').trim());

  const toggleCandidate = (candidate) => {
    setSelected(prev => prev.some(item => item.id === candidate.id)
      ? prev.filter(item => item.id !== candidate.id)
      : [...prev, candidate]);
  };

  const submit = async () => {
    if (!selected.length && !manualFormReady) {
      setInlineError('Please search and select a candidate or fill in the new candidate details');
      return;
    }
    setLoading(true);
    setInlineError('');
    try {
      const candidatesToAssign = [...selected];
      if (manualFormReady) {
        const { firstName, lastName, email, mobileNo, skill } = newCandidate;
        const res = await API.post('/users/candidates', {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          mobileNo: mobileNo.trim(),
          skill: skill.trim(),
          company: 'inteliment',
        });
        const created = res.data?.user;
        if (created?.id) candidatesToAssign.push(created);
      }
      await onAssign(candidatesToAssign);
      onClose();
    } catch (err) {
      setInlineError(err.response?.data?.message || 'Failed to add candidates.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="emx-modal-overlay" onClick={onClose}>
      <div className="emx-modal emx-assign-modal" onClick={event => event.stopPropagation()}>
        <button className="emx-modal-close" onClick={onClose} aria-label="Close">x</button>
        <div className="emx-modal-header">
          <h2>Assign Candidate</h2>
          <p>Select a candidate or add a new one.</p>
        </div>
        <div className="emx-modal-body">
          <div className="drive-assign-search-action">
            <div className="emx-search-wrap">
              <label className="emx-field-label">Candidate</label>
              <input
                className="emx-input"
                value={query}
                onChange={event => { setQuery(event.target.value); setShowDropdown(true); }}
                onFocus={() => setShowDropdown(true)}
                placeholder="Search by name, email, or ID..."
              />
              {showDropdown && query && (
                <div className="emx-candidate-dropdown">
                  {filtered.slice(0, 10).map(candidate => (
                    <button type="button" key={candidate.id} className="emx-candidate-option" onClick={() => { toggleCandidate(candidate); setQuery(''); setShowDropdown(false); }}>
                      <span>{candidate.name}</span>
                      <small>{candidate.email || candidate.mobile_no || '-'}</small>
                    </button>
                  ))}
                  {filtered.length === 0 && <div className="drive-empty">No candidates found.</div>}
                </div>
              )}
            </div>
          </div>
          {selected.length > 0 && (
            <div className="emx-selected-list">
              {selected.map(candidate => (
                <div key={candidate.id} className="emx-selected-card">
                  <span className="emx-selected-avatar">{getInitials(candidate.name)}</span>
                  <div>
                    <strong>{candidate.name}</strong>
                    <small>{candidate.email || candidate.mobile_no || '-'}</small>
                  </div>
                  <button type="button" onClick={() => toggleCandidate(candidate)}>x</button>
                </div>
              ))}
            </div>
          )}
          <div className="emx-divider">or add a new candidate</div>
          <div className="emx-candidate-form">
            <div className="emx-form-grid">
              <label><span>First Name <strong className="required-star">*</strong></span><input className="emx-input" value={newCandidate.firstName} onChange={event => setNewCandidate(prev => ({ ...prev, firstName: event.target.value }))} /></label>
              <label><span>Last Name <strong className="required-star">*</strong></span><input className="emx-input" value={newCandidate.lastName} onChange={event => setNewCandidate(prev => ({ ...prev, lastName: event.target.value }))} /></label>
              <label><span>Email <strong className="required-star">*</strong></span><input className="emx-input" type="email" value={newCandidate.email} onChange={event => setNewCandidate(prev => ({ ...prev, email: event.target.value }))} /></label>
              <label><span>Mobile No. <strong className="required-star">*</strong></span><input className="emx-input" type="tel" value={newCandidate.mobileNo} onChange={event => setNewCandidate(prev => ({ ...prev, mobileNo: event.target.value }))} /></label>
              <label><span>Primary Skill <strong className="required-star">*</strong></span><input className="emx-input" value={newCandidate.skill} placeholder="e.g. Frontend, Backend, DevOps..." onChange={event => setNewCandidate(prev => ({ ...prev, skill: event.target.value }))} /></label>
            </div>
          </div>
          <div className="emx-add-candidate-row">
            <button className="emx-btn emx-btn--ghost" type="button" onClick={onClose}>Cancel</button>
            <button className="emx-add-candidate" type="button" onClick={submit} disabled={loading}>
              {loading ? 'Adding...' : `Add ${selected.length + (manualFormReady ? 1 : 0) || ''} Candidate${selected.length + (manualFormReady ? 1 : 0) === 1 ? '' : 's'}`}
            </button>
          </div>
          {inlineError && <div style={{ color: '#dc2626', fontSize: 12, fontWeight: 700, marginTop: 10 }}>{inlineError}</div>}
        </div>
      </div>
    </div>
  );
}

function ExamPickerModal({ exams, selectedExamId, onChange, onClose }) {
  const [query, setQuery] = useState('');
  const filtered = exams.filter(exam => (exam.title || '').toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="drive-modal-backdrop" onClick={onClose}>
      <div className="drive-picker-modal" onClick={event => event.stopPropagation()}>
        <div className="drive-modal-head">
          <div>
            <h2>Select Exam</h2>
            <p>Choose one exam for this drive</p>
          </div>
          <button className="drive-icon-btn" onClick={onClose}>x</button>
        </div>
        <div className="drive-search">
          <span>⌕</span>
          <input value={query} onChange={event => setQuery(event.target.value)} placeholder="Search exams..." />
        </div>
        <div className="drive-picker-list">
          {filtered.length === 0 ? (
            <div className="drive-empty">No exams found.</div>
          ) : filtered.map(exam => (
            <button
              type="button"
              key={exam.id}
              className={`drive-picker-row ${String(selectedExamId) === String(exam.id) ? 'is-selected' : ''}`}
              onClick={() => { onChange(exam.id); onClose(); }}
            >
              <span className="drive-check">{String(selectedExamId) === String(exam.id) ? '✓' : ''}</span>
              <span>
                <strong>{exam.title}</strong>
                <small>{exam.status || 'Exam'}</small>
              </span>
            </button>
          ))}
        </div>
        <div className="drive-modal-actions">
          <button className="drive-btn drive-btn--ghost" onClick={() => { onChange(''); onClose(); }}>Clear</button>
        </div>
      </div>
    </div>
  );
}

function CreateDriveModal({ exams, onClose, onCreate, onCandidatesCreated, onNotify, mode = 'create', initialForm = emptyDriveForm }) {
  const [form, setForm] = useState(() => ({
    ...emptyDriveForm,
    ...initialForm,
    candidateIds: Array.isArray(initialForm.candidateIds) ? initialForm.candidateIds : [],
  }));
  const [showCandidateChoice, setShowCandidateChoice] = useState(false);
  const [showChooseCandidates, setShowChooseCandidates] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showExams, setShowExams] = useState(false);
  const [formError, setFormError] = useState('');
  const selectedExam = exams.find(exam => String(exam.id) === String(form.examId));
  const canCreate = form.name.trim() && form.date && form.venue.trim();

  const getMinTime = () => {
    if (!form.date) return undefined;
    const today = new Date();
    const selected = new Date(form.date);
    today.setHours(0, 0, 0, 0);
    selected.setHours(0, 0, 0, 0);
    if (selected.getTime() === today.getTime()) {
      const h = String(new Date().getHours()).padStart(2, '0');
      const m = String(new Date().getMinutes()).padStart(2, '0');
      return `${h}:${m}`;
    }
    return undefined;
  };

  const update = (key, value) => {
    setForm(previous => ({ ...previous, [key]: value }));
    setFormError('');
  };

  const handleSubmit = () => {
    const minTime = getMinTime();
    if (!form.examId) {
      setFormError('Please select an exam before creating the drive');
      return;
    }
    if ((minTime && form.startTime && form.startTime < minTime) || (minTime && form.endTime && form.endTime < minTime)) {
      setFormError('Drive time cannot be before the current time');
      return;
    }
    if (form.startTime && form.endTime && form.startTime >= form.endTime) {
      setFormError('End time must be after start time');
      return;
    }
    onCreate(form);
  };

  const handleCandidatesCreated = (createdCandidates) => {
    const createdIds = (createdCandidates || []).map(candidate => candidate.id).filter(Boolean);
    if (createdIds.length > 0) {
      setForm(previous => ({
        ...previous,
        candidateIds: [...new Set([...previous.candidateIds, ...createdIds])],
      }));
      onCandidatesCreated?.(createdCandidates);
    }
  };

  return (
    <div className="drive-modal-backdrop" onClick={() => onClose(form)}>
      <div className="drive-create-modal" onClick={event => event.stopPropagation()}>
        <div className="drive-modal-head">
          <div>
            <h2>{mode === 'edit' ? 'Edit Drive' : 'Create Drive'}</h2>
            <p>{mode === 'edit' ? 'Update the drive details. Candidates can be added after saving the drive.' : 'Add the drive details now. Candidates can be added after creating the drive.'}</p>
          </div>
          <button className="drive-icon-btn" onClick={() => onClose(form)}>x</button>
        </div>
        <div className="drive-form-grid">
          <label className="drive-field">
            <span>Drive Name <strong className="drive-required">*</strong></span>
            <input value={form.name} onChange={event => update('name', event.target.value)} placeholder="e.g. Campus Hiring Drive" />
          </label>
          <label className="drive-field">
            <span>Date <strong className="drive-required">*</strong></span>
            <input type="date" min={todayInputValue()} value={form.date} onChange={event => update('date', event.target.value)} />
          </label>
          <DriveTimeSelect label="Start Time" value={form.startTime} minTime={getMinTime()} onChange={(value) => update('startTime', value)} />
          <DriveTimeSelect label="End Time" value={form.endTime} minTime={getMinTime()} onChange={(value) => update('endTime', value)} />
          <label className="drive-field">
            <span>Venue <strong className="drive-required">*</strong></span>
            <input value={form.venue} onChange={event => update('venue', event.target.value)} placeholder="Venue or virtual link" />
          </label>
        </div>
        <div className="drive-attach-row">
          <button className="drive-attach-card" onClick={() => setShowCandidateChoice(true)}>
            <span>Add Candidates</span>
            <strong>{form.candidateIds.length ? `${form.candidateIds.length} selected` : 'Optional'}</strong>
          </button>
          <button className="drive-attach-card" onClick={() => setShowExams(true)}>
            <span>Select Exam <strong className="drive-required">*</strong></span>
            <strong>{selectedExam?.title || 'Required'}</strong>
          </button>
        </div>
        {formError && <div style={{ color: '#dc2626', fontSize: 12, fontWeight: 700, marginTop: 12 }}>{formError}</div>}
        <div className="drive-modal-actions">
          <button className="drive-btn drive-btn--ghost" onClick={onClose}>Cancel</button>
          <button className="drive-btn drive-btn--primary" disabled={!canCreate} onClick={handleSubmit}>{mode === 'edit' ? 'Save Changes' : 'Create'}</button>
        </div>
        {showCandidateChoice && (
          <AddCandidateChoiceModal
            onClose={() => setShowCandidateChoice(false)}
            onSingleEntry={() => { setShowCandidateChoice(false); setShowChooseCandidates(true); }}
            onBulkUpload={() => { setShowCandidateChoice(false); setShowBulkUpload(true); }}
          />
        )}
        {showChooseCandidates && (
          <DriveChooseCandidatesModal
            onClose={() => setShowChooseCandidates(false)}
            onAssign={(selected) => { handleCandidatesCreated(selected); setShowChooseCandidates(false); }}
          />
        )}
        {showBulkUpload && (
          <DriveBulkUploadForm
            onClose={() => setShowBulkUpload(false)}
            onCreated={handleCandidatesCreated}
            onNotify={onNotify}
          />
        )}
        {showExams && (
          <ExamPickerModal
            exams={exams}
            selectedExamId={form.examId}
            onChange={(id) => update('examId', id)}
            onClose={() => setShowExams(false)}
          />
        )}
      </div>
    </div>
  );
}

const formatViolationLabel = (submitReason, tabSwitchCount) => {
  if (submitReason === 'tab_switch') return `Tab switch (${tabSwitchCount || 0})`;
  if (submitReason === 'screen_share_stopped') return 'Screen share stopped';
  if (submitReason === 'timeout') return 'Timed out';
  if (submitReason === 'manual') return 'None';
  return submitReason ? String(submitReason).replace(/_/g, ' ') : 'None';
};

const isDriveCandidateDefaulter = (candidate = {}) => (
  String(candidate.result_status || candidate.assignment_result_status || candidate.attempt_result_status || '').toLowerCase() === 'defaulter' ||
  String(candidate.result_status || candidate.assignment_result_status || candidate.attempt_result_status || '').toLowerCase() === 'disqualified' ||
  candidate.submit_reason === 'tab_switch' ||
  Number(candidate.tab_switch_count || 0) >= 2 ||
  String(candidate.status || '').toLowerCase() === 'defaulter'
);

function DriveCandidateDetailModal({ candidate, loadingAnswers, loadingScreenshots, onClose, onExport, onOpenScreenshot, answers, screenshots }) {
  if (!candidate) return null;
  const sectionScores = candidate.section_scores || [];
  const displayStatus = isDriveCandidateDefaulter(candidate) ? 'Defaulter' : (candidate.status || '-');
  return (
    <div className="drive-modal-backdrop drive-candidate-detail-backdrop" onClick={onClose}>
      <div className="drive-candidate-detail-modal" onClick={event => event.stopPropagation()}>
        <div className="drive-candidate-detail-head">
          <div>
            <h2>{candidate.name}</h2>
            <p>Candidate attempt details</p>
          </div>
          <div className="drive-candidate-detail-actions">
            <button type="button" onClick={onExport}>Export CSV</button>
            <button type="button" className="drive-candidate-detail-close" onClick={onClose}>x</button>
          </div>
        </div>
        <div className="drive-candidate-summary-grid">
          {[
            ['Email', candidate.email || '-'],
            ['Mobile No.', candidate.mobile_no || '-'],
            ['College', candidate.college_name || '-'],
            ['Degree', candidate.degree || '-'],
            ['Exam Attempted', candidate.exam_title || '-'],
            ['Status', displayStatus],
            ['Score', candidate.total_score != null ? `${Math.round(candidate.total_score)}%` : '-'],
            ['Submitted On', candidate.submitted_at ? new Date(candidate.submitted_at).toLocaleString('en-IN') : '-'],
            ['Violation', formatViolationLabel(candidate.submit_reason, candidate.tab_switch_count)],
            ['Tab Switches', candidate.tab_switch_count ?? 0],
          ].map(([label, value]) => (
            <div key={label}>
              <span>{label}</span>
              <strong title={String(value)}>{value}</strong>
            </div>
          ))}
        </div>
        <div className="drive-candidate-detail-body">
          <div className="drive-answer-panel">
            <div className="drive-answer-heading">
              <h3>Question Answers</h3>
              {sectionScores.length > 0 && (
                <div className="drive-section-score-pills">
                  {sectionScores.map(section => (
                    <span key={section.name}>{section.name} <strong>{section.score ?? 0}/{section.total ?? 0}</strong></span>
                  ))}
                </div>
              )}
            </div>
            {loadingAnswers ? (
              <p className="drive-muted">Loading answers...</p>
            ) : answers.length === 0 ? (
              <p className="drive-muted">No question answers found for this candidate.</p>
            ) : (
              <div className="drive-answer-table-wrap">
                <table className="drive-answer-table">
                  <thead>
                    <tr>
                      <th>Question</th>
                      <th>Selected</th>
                      <th>Correct</th>
                      <th>Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {answers.map((answer, index) => {
                      const resultLabel = answer.is_correct === true ? 'Correct' : answer.is_correct === false ? 'Incorrect' : 'Not Answered';
                      return (
                        <tr key={`${answer.question_id}-${index}`}>
                          <td>{answer.question_text || '-'}</td>
                          <td>{answer.selected_option || '-'}</td>
                          <td>{answer.correct_option || '-'}</td>
                          <td><span className={`drive-answer-result drive-answer-result--${resultLabel.toLowerCase().replace(/\s+/g, '-')}`}>{resultLabel}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="drive-screenshots-panel">
            <h3>Screenshots</h3>
            {loadingScreenshots ? (
              <p className="drive-muted">Loading screenshots...</p>
            ) : screenshots.length ? (
              <div className="drive-screenshot-list">
                {screenshots.map(filename => (
                  <div className="drive-screenshot-row" key={filename}>
                    <span>{filename}</span>
                    <button type="button" onClick={() => onOpenScreenshot(filename)}>Open</button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="drive-muted">No screenshots found for this candidate.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DrivePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const storedUser = getStoredUserForRole('recruiter');
  const [drives, setDrives] = useState([]);
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dateFilter, setDateFilter] = useState('All Dates');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [detailPage, setDetailPage] = useState(1);
  const [detailPageSize, setDetailPageSize] = useState(10);
  const [showCreate, setShowCreate] = useState(false);
  const [editingDrive, setEditingDrive] = useState(null);
  const [draftDriveForm, setDraftDriveForm] = useState(null);
  const [addingCandidatesDrive, setAddingCandidatesDrive] = useState(null);
  const [showAddChoose, setShowAddChoose] = useState(false);
  const [showAddBulkUpload, setShowAddBulkUpload] = useState(false);
  const [activeDriveDetail, setActiveDriveDetail] = useState(null);
  const [driveDetailLoading, setDriveDetailLoading] = useState(false);
  const [selectedCandidateIds, setSelectedCandidateIds] = useState(new Set());
  const [candidateDetail, setCandidateDetail] = useState(null);
  const [candidateAnswers, setCandidateAnswers] = useState([]);
  const [candidateScreenshots, setCandidateScreenshots] = useState([]);
  const [candidateAnswersLoading, setCandidateAnswersLoading] = useState(false);
  const [candidateScreenshotsLoading, setCandidateScreenshotsLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [driveSort, setDriveSort] = useState({ key: 'name', dir: 'asc' });
  const [driveCandidateSort, setDriveCandidateSort] = useState({ key: 'name', dir: 'asc' });
  const [driveCandidateStatusFilter, setDriveCandidateStatusFilter] = useState('All Status');
  const [showDriveStatusMenu, setShowDriveStatusMenu] = useState(false);
  const routeDriveId = useMemo(() => new URLSearchParams(location.search).get('driveId'), [location.search]);
  const [otp, setOtp] = useState(null);
  const [otpExpiresAt, setOtpExpiresAt] = useState(null);
  const [otpGenerating, setOtpGenerating] = useState(false);
  const [otpTick, setOtpTick] = useState(Date.now());
  const [driveToDelete, setDriveToDelete] = useState(null);
  const [driveDeleting, setDriveDeleting] = useState(false);
  const [driveDeleteError, setDriveDeleteError] = useState('');

  const fetchDrives = async ({ suppressErrorToast = false } = {}) => {
    setLoading(true);
    try {
      const res = await API.get('/drives');
      setDrives(Array.isArray(res.data?.drives) ? res.data.drives : []);
    } catch {
      if (!suppressErrorToast) setToast({ message: 'Failed to load drives.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrives();
    API.get('/exam').then(res => setExams(Array.isArray(res.data) ? res.data : [])).catch(() => setExams([]));
  }, []);

  useEffect(() => {
    const handleFocus = () => {
      fetchDrives({ suppressErrorToast: true });
      if (activeDriveDetail?.drive?.id) loadDriveDetail(activeDriveDetail.drive.id);
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [activeDriveDetail?.drive?.id]);

  const dateOptions = ['All Dates', 'Today', 'Upcoming', 'Past'];
  const filteredDrives = useMemo(() => {
    const searchTerm = search.trim().toLowerCase();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const filtered = drives.filter(drive => {
      const text = [
        drive.name, drive.venue, drive.exam_title,
        drive.drive_date ? formatDate(drive.drive_date) : '',
        drive.drive_time, drive.end_time, drive.candidate_count,
      ].filter(value => value !== null && value !== undefined).join(' ').toLowerCase();
      if (searchTerm && !text.includes(searchTerm)) return false;
      if (dateFilter === 'All Dates') return true;
      const date = new Date(drive.drive_date);
      date.setHours(0, 0, 0, 0);
      if (dateFilter === 'Today') return date.getTime() === today.getTime();
      if (dateFilter === 'Upcoming') return date.getTime() >= today.getTime();
      if (dateFilter === 'Past') return date.getTime() < today.getTime();
      return true;
    });
    return [...filtered].sort((a, b) => {
      const dir = driveSort.dir === 'asc' ? 1 : -1;
      const av = driveSort.key === 'date' ? new Date(a.drive_date).getTime() : String(a.name || '').toLowerCase();
      const bv = driveSort.key === 'date' ? new Date(b.drive_date).getTime() : String(b.name || '').toLowerCase();
      return av > bv ? dir : av < bv ? -dir : 0;
    });
  }, [dateFilter, driveSort, drives, search]);

  const totalPages = Math.max(1, Math.ceil(filteredDrives.length / pageSize));
  const paginatedDrives = filteredDrives.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleCreate = async (form) => {
    try {
      const payload = {
        name: form.name,
        date: form.date,
        startTime: form.startTime || null,
        endTime: form.endTime || null,
        venue: form.venue,
        examId: form.examId || null,
        candidateIds: form.candidateIds,
      };
      const isEditing = editingDrive?.id;
      if (isEditing) {
        await API.put(`/drives/${isEditing}`, payload);
      } else {
        await API.post('/drives', payload);
      }
      setShowCreate(false);
      setEditingDrive(null);
      setDraftDriveForm(null);
      setToast({ message: isEditing ? 'Drive updated successfully.' : 'Drive created successfully.', type: 'success' });
      await fetchDrives({ suppressErrorToast: true });
      if (isEditing && activeDriveDetail?.drive?.id === isEditing) await loadDriveDetail(isEditing);
    } catch (err) {
      setToast({ message: err.response?.data?.message || 'Failed to save drive.', type: 'error' });
    }
  };

  const handleEditDrive = (drive) => {
    setEditingDrive(drive);
    setShowCreate(true);
  };

  const handleDeleteDrive = (drive) => {
    if (!drive?.id) return;
    setDriveToDelete(drive);
    setDriveDeleteError('');
  };

  const handleDeleteDriveConfirm = async () => {
    if (!driveToDelete?.id) return;
    setDriveDeleting(true);
    setDriveDeleteError('');
    try {
      await API.delete(`/drives/${driveToDelete.id}`);
      setToast({ message: 'Drive deleted successfully.', type: 'success' });
      if (activeDriveDetail?.drive?.id === driveToDelete.id) {
        setActiveDriveDetail(null);
        navigate('/drives');
      }
      setDriveToDelete(null);
      await fetchDrives();
    } catch (err) {
      setDriveDeleteError(err.response?.data?.message || 'Failed to delete drive.');
    } finally {
      setDriveDeleting(false);
    }
  };

  const openAddCandidates = (drive) => {
    setAddingCandidatesDrive(drive);
  };

  const attachCreatedCandidatesToDrive = async (createdCandidates) => {
    const driveId = addingCandidatesDrive?.id || activeDriveDetail?.drive?.id;
    const candidateIds = (createdCandidates || []).map(candidate => candidate.id).filter(Boolean);
    if (!driveId || candidateIds.length === 0) return;
    try {
      const res = await API.post(`/drives/${driveId}/candidates`, { candidateIds });
      const added = Number(res.data?.added ?? candidateIds.length);
      setToast({ message: `${added} candidate${added !== 1 ? 's' : ''} added. Welcome emails have been sent.`, type: 'success' });
      setAddingCandidatesDrive(null);
      setShowAddChoose(false);
      setShowAddBulkUpload(false);
      await fetchDrives();
      if (activeDriveDetail?.drive?.id === driveId) await loadDriveDetail(driveId);
    } catch (err) {
      setToast({ message: err.response?.data?.message || 'Failed to add candidates.', type: 'error' });
    }
  };

  const attachExistingCandidatesToDrive = async (selectedCandidatesForDrive) => {
    const driveId = addingCandidatesDrive?.id || activeDriveDetail?.drive?.id;
    const candidateIds = (selectedCandidatesForDrive || []).map(candidate => candidate.id).filter(Boolean);
    if (!driveId || candidateIds.length === 0) return;
    try {
      const res = await API.post(`/drives/${driveId}/candidates`, { candidateIds });
      const added = Number(res.data?.added ?? candidateIds.length);
      setToast({ message: `${added} candidate${added !== 1 ? 's' : ''} added. Welcome emails have been sent.`, type: 'success' });
      setAddingCandidatesDrive(null);
      setShowAddChoose(false);
      await fetchDrives();
      if (activeDriveDetail?.drive?.id === driveId) await loadDriveDetail(driveId);
    } catch (err) {
      setToast({ message: err.response?.data?.message || 'Failed to add candidates.', type: 'error' });
    }
  };

  const loadDriveDetail = async (driveId) => {
    setDriveDetailLoading(true);
    try {
      const [res, otpRes] = await Promise.all([
        API.get(`/drives/${driveId}`),
        API.get(`/drives/${driveId}/otps`).catch(() => ({ data: { otps: [] } })),
      ]);
      setActiveDriveDetail({
        drive: res.data?.drive,
        sections: Array.isArray(res.data?.sections) ? res.data.sections : [],
        candidates: Array.isArray(res.data?.candidates) ? res.data.candidates : [],
      });
      const activeOtp = (Array.isArray(otpRes.data?.otps) ? otpRes.data.otps : []).find((entry) => {
        const expiresAt = new Date(entry.expires_at || entry.expiresAt).getTime();
        return !entry.is_used && expiresAt > Date.now();
      });
      setOtp(activeOtp?.otp_code || null);
      setOtpExpiresAt(activeOtp?.expires_at || null);
    } catch {
      setToast({ message: 'Failed to load drive details.', type: 'error' });
    } finally {
      setDriveDetailLoading(false);
    }
  };

  useEffect(() => {
    if (routeDriveId) {
      setSelectedCandidateIds(new Set());
      setDetailPage(1);
      loadDriveDetail(routeDriveId);
      return;
    }
    setActiveDriveDetail(null);
    setSelectedCandidateIds(new Set());
    setOtp(null);
    setOtpExpiresAt(null);
  }, [routeDriveId]);

  const openDriveDetail = async (drive) => {
    setSelectedCandidateIds(new Set());
    setDetailPage(1);
    navigate(`/drives?driveId=${drive.id}`);
  };

  useEffect(() => {
    if (!otpExpiresAt) return undefined;
    const timer = window.setInterval(() => setOtpTick(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [otpExpiresAt]);

  useEffect(() => {
    const driveId = activeDriveDetail?.drive?.id;
    if (!driveId) return undefined;
    const poll = window.setInterval(async () => {
      try {
        const res = await API.get(`/drives/${driveId}/candidate-statuses`);
        const statuses = Array.isArray(res.data?.statuses) ? res.data.statuses : [];
        setActiveDriveDetail(prev => {
          if (!prev?.candidates) return prev;
          const statusMap = new Map(statuses.map(status => [Number(status.user_id), status.status]));
          let changed = false;
          const candidates = prev.candidates.map(candidate => {
            const newStatus = statusMap.get(Number(candidate.id));
            if (newStatus && newStatus !== candidate.status) {
              changed = true;
              return { ...candidate, status: newStatus };
            }
            return candidate;
          });
          return changed ? { ...prev, candidates } : prev;
        });
      } catch {
        // Silent background polling
      }
    }, 5000);
    return () => window.clearInterval(poll);
  }, [activeDriveDetail?.drive?.id]);

  const otpRemainingSeconds = otpExpiresAt
    ? Math.max(0, Math.floor((new Date(otpExpiresAt).getTime() - otpTick) / 1000))
    : 0;
  const otpExpired = Boolean(otpExpiresAt && otpRemainingSeconds <= 0);
  const otpCountdown = `${String(Math.floor(otpRemainingSeconds / 60)).padStart(2, '0')}:${String(otpRemainingSeconds % 60).padStart(2, '0')}`;
  const otpActionDisabled = otpGenerating || Boolean(otp && !otpExpired);

  const handleGenerateOtp = async () => {
    const driveId = activeDriveDetail?.drive?.id;
    if (!driveId) return;
    setOtpGenerating(true);
    try {
      const res = await API.post(`/drives/${driveId}/generate-otp`);
      setOtp(res.data?.otp || null);
      setOtpExpiresAt(res.data?.expiresAt || null);
      setOtpTick(Date.now());
      setToast({ message: 'OTP generated successfully.', type: 'success' });
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to generate OTP.';
      if (err.response?.status === 400 && err.response?.data?.existingExpiresAt) {
        setOtp(null);
        setOtpExpiresAt(err.response.data.existingExpiresAt);
        setOtpTick(Date.now());
        setToast({ message: 'An active OTP is already running. Share the current code or wait for it to expire.', type: 'error' });
        return;
      }
      setToast({ message, type: 'error' });
    } finally {
      setOtpGenerating(false);
    }
  };

  const toggleCandidateSelection = (candidateId) => {
    const candidate = detailCandidates.find(item => item.id === candidateId);
    if (!candidate || isDriveCandidateDefaulter(candidate)) return;
    setSelectedCandidateIds(prev => {
      const next = new Set(prev);
      if (next.has(candidateId)) next.delete(candidateId);
      else next.add(candidateId);
      return next;
    });
  };

  const detailCandidates = useMemo(() => activeDriveDetail?.candidates || [], [activeDriveDetail]);
  const detailSections = activeDriveDetail?.sections || [];
  const filteredDetailCandidates = useMemo(() => {
    const filtered = driveCandidateStatusFilter === 'All Status'
      ? detailCandidates
      : detailCandidates.filter(candidate => String(candidate.status || 'Added') === driveCandidateStatusFilter);
    return [...filtered].sort((a, b) => {
      const dir = driveCandidateSort.dir === 'asc' ? 1 : -1;
      const readScore = (candidate) => Number(candidate.total_score ?? -1);
      const av = driveCandidateSort.key === 'score' ? readScore(a) : driveCandidateSort.key === 'status' ? String(a.status || '') : String(a.name || '').toLowerCase();
      const bv = driveCandidateSort.key === 'score' ? readScore(b) : driveCandidateSort.key === 'status' ? String(b.status || '') : String(b.name || '').toLowerCase();
      return av > bv ? dir : av < bv ? -dir : 0;
    });
  }, [detailCandidates, driveCandidateSort, driveCandidateStatusFilter]);

  const detailTotalPages = Math.max(1, Math.ceil(filteredDetailCandidates.length / detailPageSize));
  const paginatedDetailCandidates = filteredDetailCandidates.slice((detailPage - 1) * detailPageSize, detailPage * detailPageSize);
  const selectedCandidates = detailCandidates.filter(candidate => selectedCandidateIds.has(candidate.id));

  const canShortlistSelected = selectedCandidates.length > 0
    && selectedCandidates.every(candidate => candidate.status === 'Completed' && !isDriveCandidateDefaulter(candidate));

  const canRejectSelected = selectedCandidates.length > 0
    && selectedCandidates.every(candidate => candidate.status === 'Completed' && !isDriveCandidateDefaulter(candidate));

  const toggleDriveSort = (key) => setDriveSort(prev => ({ key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' }));
  const toggleDriveCandidateSort = (key) => setDriveCandidateSort(prev => ({ key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' }));

  const handleSelectFilteredCandidates = () => {
    setSelectedCandidateIds(new Set(filteredDetailCandidates.filter(candidate => !isDriveCandidateDefaulter(candidate)).map(candidate => candidate.id)));
  };

  const handleShortlistCandidates = async () => {
    if (!activeDriveDetail?.drive?.id || !canShortlistSelected) return;
    const candidateIds = selectedCandidates.filter(candidate => !isDriveCandidateDefaulter(candidate)).map(candidate => candidate.id);
    try {
      await API.post(`/drives/${activeDriveDetail.drive.id}/publish-results`, { candidateIds });
      setToast({ message: 'Shortlist completed.', type: 'success' });
      setSelectedCandidateIds(new Set());
      await loadDriveDetail(activeDriveDetail.drive.id);
    } catch (err) {
      setToast({ message: err.response?.data?.message || 'Shortlist failed.', type: 'error' });
    }
  };

  const handleRejectCandidates = async () => {
    if (!activeDriveDetail?.drive?.id || !canRejectSelected) return;
    const allCompleted = detailCandidates.filter(
      c => c.status === 'Completed' && !isDriveCandidateDefaulter(c)
    );
    const shortlistIds = allCompleted.filter(c => !selectedCandidateIds.has(c.id)).map(c => c.id);
    try {
      await API.post(`/drives/${activeDriveDetail.drive.id}/publish-results`, { candidateIds: shortlistIds });
      setToast({ message: 'Results published. Selected candidates rejected.', type: 'success' });
      setSelectedCandidateIds(new Set());
      await loadDriveDetail(activeDriveDetail.drive.id);
    } catch (err) {
      setToast({ message: err.response?.data?.message || 'Failed to publish results.', type: 'error' });
    }
  };

  const handleOpenCandidateDetail = (candidate) => {
    setCandidateDetail({ ...candidate, exam_title: activeDriveDetail?.drive?.exam_title || '-' });
    setCandidateAnswers([]);
    setCandidateScreenshots([]);
    setCandidateAnswersLoading(true);
    setCandidateScreenshotsLoading(true);

    const examId = activeDriveDetail?.drive?.exam_id;
    const driveId = activeDriveDetail?.drive?.id;

    if (examId) {
      API.get(`/exam/${examId}/candidates/${candidate.id}/answers`)
        .then(res => setCandidateAnswers(Array.isArray(res.data?.answers) ? res.data.answers : []))
        .catch(() => setCandidateAnswers([]))
        .finally(() => setCandidateAnswersLoading(false));
    } else {
      setCandidateAnswersLoading(false);
    }

    if (candidate.attempt_id && examId) {
      API.get(`/exam/screenshots/${candidate.id}`, { params: { examId } })
        .then(res => {
          const screenshots = Array.isArray(res.data?.screenshots)
            ? res.data.screenshots.map(entry => (typeof entry === 'string' ? entry : entry?.filename)).filter(Boolean)
            : [];
          setCandidateScreenshots(screenshots);
        })
        .catch(() => setCandidateScreenshots([]))
        .finally(() => setCandidateScreenshotsLoading(false));
    } else {
      setCandidateScreenshots([]);
      setCandidateScreenshotsLoading(false);
    }

    if (driveId) {
      loadDriveDetail(driveId).then(() => {
        setActiveDriveDetail(prev => {
          const fresh = prev?.candidates?.find(c => c.id === candidate.id);
          if (fresh) {
            setCandidateDetail(prev2 => prev2 ? {
              ...prev2,
              section_scores: fresh.section_scores,
              tab_switch_count: fresh.tab_switch_count,
              submit_reason: fresh.submit_reason,
              total_score: fresh.total_score,
              status: fresh.status,
              attempt_id: fresh.attempt_id,
            } : prev2);
            if (fresh.attempt_id && !candidate.attempt_id && examId) {
              setCandidateScreenshotsLoading(true);
              API.get(`/exam/screenshots/${candidate.id}`, { params: { examId } })
                .then(res => {
                  const screenshots = Array.isArray(res.data?.screenshots)
                    ? res.data.screenshots.map(entry => (typeof entry === 'string' ? entry : entry?.filename)).filter(Boolean)
                    : [];
                  setCandidateScreenshots(screenshots);
                })
                .catch(() => setCandidateScreenshots([]))
                .finally(() => setCandidateScreenshotsLoading(false));
            }
          }
          return prev;
        });
      });
    }
  };

  const handleOpenDriveScreenshot = async (filename) => {
    try {
      const response = await API.get(`/exam/screenshot/${encodeURIComponent(filename)}`, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(response.data);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60 * 1000);
    } catch (err) {
      setToast({ message: err.response?.data?.message || 'Unable to open screenshot.', type: 'error' });
    }
  };

  const handleExportCandidateCsv = () => {
    if (!candidateDetail) return;
    const escapeCsv = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const rows = [
      ['Field', 'Value'],
      ['Name', candidateDetail.name],
      ['Email', candidateDetail.email],
      ['Status', candidateDetail.status],
      ['Score', candidateDetail.total_score != null ? `${candidateDetail.total_score}%` : '-'],
      [],
      ['Question', 'Selected Answer', 'Correct Answer', 'Result'],
      ...candidateAnswers.map(answer => [
        answer.question_text || '',
        answer.selected_option || 'Not answered',
        answer.correct_option || '',
        answer.is_correct === true ? 'Correct' : answer.is_correct === false ? 'Incorrect' : 'Not answered',
      ]),
    ];
    const csv = rows.map(row => row.map(escapeCsv).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${(candidateDetail.name || 'candidate').replace(/[^a-z0-9_-]+/gi, '_')}_details.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="nr-page drive-page">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="nr-navbar-wrapper">
        <img src={logo} alt="Inteliment Logo" className="nr-navbar-logo-img" />
        <div className="nr-navbar-greeting">
          <h1 className="nr-navbar-greeting-title">Drive</h1>
          <p className="nr-navbar-greeting-sub">{drives.length} drives created</p>
        </div>
        <div className="nr-navbar-actions">
          <button className="nr-navbar-add-btn" onClick={() => { setEditingDrive(null); setShowCreate(true); }}>
            <span className="nr-navbar-add-icon">+</span>
            Create Drive
          </button>
          <div className="nr-navbar-profile">
            <ProfileDropdown user={storedUser.name || storedUser.email} email={storedUser.email} />
          </div>
        </div>
      </div>

      <aside className="nr-sidebar">
        {sidebarItems
          .filter(item => !(storedUser.role === 'junior_recruiter' && item.path === '/question-bank'))
          .map(({ label, Icon, path }, index) => {
            const isActive = location.pathname === path;
            return (
              <div
                key={label}
                onClick={() => navigate(path)}
                className={`nr-sidebar-item ${isActive ? 'active' : ''}`}
                style={{ top: `${140 + index * 64}px` }}
              >
                <span className="nr-sidebar-icon"><Icon active={isActive} /></span>
                <span className="nr-sidebar-text">{label}</span>
              </div>
            );
          })}
      </aside>

      <main className={`nr-main drive-main ${activeDriveDetail ? 'drive-main--detail' : ''}`}>
        {!activeDriveDetail ? (
          <>
            <div className="drive-toolbar">
              <div className="drive-search">
                <span>⌕</span>
                <input value={search} onChange={event => { setSearch(event.target.value); setCurrentPage(1); }} placeholder="Search drives..." />
              </div>
              <div className="drive-filter drive-filter--candidate-style">
                <div className="cp-filter-title drive-filter-title">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M4 7H20M7 12H17M10 17H14" stroke="#64748B" strokeWidth="1.8" strokeLinecap="round"/>
                  </svg>
                  <span>Filters</span>
                </div>
                <div className="cp-filter-group drive-date-filter">
                  <div className="cp-select-wrap">
                    <PrettySelect
                      value={dateFilter}
                      options={dateOptions}
                      onChange={(value) => { setDateFilter(value); setCurrentPage(1); }}
                      icon={(
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M7 3V6M17 3V6M4.5 9H19.5M6 5H18C19.1 5 20 5.9 20 7V18C20 19.1 19.1 20 18 20H6C4.9 20 4 19.1 4 18V7C4 5.9 4.9 5 6 5Z" stroke="#7AA2F7" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>

            <h2 className="drive-section-heading">Drives</h2>

            <div className="drive-table-card">
              <div className="drive-table-header">
                <span>Sr No.</span>
                <button type="button" className="drive-sort-header" onClick={() => toggleDriveSort('name')}>Drive Name <span>↕</span></button>
                <button type="button" className="drive-sort-header" onClick={() => toggleDriveSort('date')}>Date <span>↕</span></button>
                <span>Time</span>
                <span>Venue</span>
                <span>No. of Candidates</span>
                <span>Actions</span>
              </div>
              <div className="drive-table-body">
                {loading ? (
                  <div className="drive-empty">Loading drives...</div>
                ) : paginatedDrives.length === 0 ? (
                  <div className="drive-empty">No drives found.</div>
                ) : paginatedDrives.map((drive, index) => (
                  <div className="drive-table-row" key={drive.id}>
                    <span>{(currentPage - 1) * pageSize + index + 1}</span>
                    <span className="drive-name-cell">{drive.name}</span>
                    <span>{formatDate(drive.drive_date)}</span>
                    <span>{formatTimeRange(drive.drive_time, drive.end_time)}</span>
                    <span className="drive-venue-cell">{drive.venue}</span>
                    <span>{drive.candidate_count || 0}</span>
                    <span>
                      <div className="drive-list-actions">
                        <button className="drive-action-icon" onClick={() => openDriveDetail(drive)} title="View drive"><IconEyeAction /></button>
                        <button className="drive-action-icon" onClick={() => handleEditDrive(drive)} title="Edit drive"><IconEditAction /></button>
                        <button className="drive-action-icon" onClick={() => openAddCandidates(drive)} title="Add candidates"><IconPlusAction /></button>
                        {storedUser.role !== 'junior_recruiter' && <button className="drive-action-icon drive-action-icon--danger" onClick={() => handleDeleteDrive(drive)} title="Delete drive"><IconDeleteAction /></button>}
                      </div>
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {!loading && filteredDrives.length > 0 && (
              <div className="drive-pagination">
                <div className="drive-page-controls">
                  <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>{'<<'} Prev</button>
                  <button onClick={() => setCurrentPage(page => Math.max(1, page - 1))} disabled={currentPage === 1}>{'<'} Prev</button>
                  {Array.from({ length: totalPages }, (_, index) => index + 1).map(page => (
                    <button key={page} className={page === currentPage ? 'active' : ''} onClick={() => setCurrentPage(page)}>{page}</button>
                  ))}
                  <button onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))} disabled={currentPage === totalPages}>Next {'>'}</button>
                  <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>Next {'>>'}</button>
                </div>
                <div className="drive-page-size">
                  <PrettySelect value={String(pageSize).padStart(2, '0')} options={['05', '10', '15']} onChange={value => { setPageSize(Number(value)); setCurrentPage(1); }} />
                  <span>Items per page</span>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="drive-detail-top">
              <div className="drive-detail-title-row">
                <button className="drive-detail-heading" onClick={() => { setActiveDriveDetail(null); setSelectedCandidateIds(new Set()); navigate('/drives'); }}>
                  <span>Drives</span>
                </button>
                <span className="drive-detail-divider">{'>'}</span>
                <strong>{activeDriveDetail.drive?.name || 'View Drive'}</strong>
              </div>
              <div className="drive-detail-actions-top">
                <button
                  className={`drive-secondary-action drive-otp-action ${otp && !otpExpired ? 'drive-otp-action--active' : ''}`}
                  onClick={handleGenerateOtp}
                  disabled={otpActionDisabled}
                  title={otp && !otpExpired ? `OTP valid for ${otpCountdown}` : otpExpired ? 'Regenerate OTP' : 'Generate OTP'}
                >
                  {otp && !otpExpired ? (
                    <>
                      <strong>{otp}</strong>
                      <svg width="15" height="15" viewBox="0 0 20 20" fill="none">
                        <path d="M16.7 9.2A6.7 6.7 0 1 1 14.7 4.5M16.7 3.3v5h-5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </>
                  ) : (
                    <>
                      <span>{otpGenerating ? 'Generating...' : otpExpired ? 'Regenerate' : 'Generate OTP'}</span>
                      {otpExpired && (
                        <svg width="15" height="15" viewBox="0 0 20 20" fill="none">
                          <path d="M16.7 9.2A6.7 6.7 0 1 1 14.7 4.5M16.7 3.3v5h-5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </>
                  )}
                </button>
                <button className="drive-secondary-action" onClick={() => handleEditDrive(activeDriveDetail.drive)}><IconEditAction /> Edit</button>
                <button className="drive-secondary-action" onClick={() => openAddCandidates(activeDriveDetail.drive)}><IconPlusAction /> Add Candidates</button>
              </div>
            </div>

            <div className="drive-detail-summary">
              <div><span>Drive</span><strong>{activeDriveDetail.drive?.name || '-'}</strong></div>
              <div><span>Date</span><strong>{formatDate(activeDriveDetail.drive?.drive_date)}</strong></div>
              <div><span>Time</span><strong>{formatTimeRange(activeDriveDetail.drive?.drive_time, activeDriveDetail.drive?.end_time)}</strong></div>
              <div><span>Venue</span><strong>{activeDriveDetail.drive?.venue || '-'}</strong></div>
              <div><span>Exam</span><strong>{activeDriveDetail.drive?.exam_title || '-'}</strong></div>
              <div><span>No. of Candidates</span><strong>{detailCandidates.length}</strong></div>
            </div>

            <div className="drive-candidates-card">
              {driveDetailLoading ? (
                <div className="drive-empty">Loading candidates...</div>
              ) : (
                <div className="drive-candidate-table-wrap">
                  <table className="drive-candidate-table">
                    <thead>
                      <tr>
                        <th>Sr. No</th>
                        <th><button type="button" className="drive-th-control" onClick={() => toggleDriveCandidateSort('name')}>Candidate Name <span>↕</span></button></th>
                        {detailSections.map(section => <th key={section} className="drive-score-col"><button type="button" className="drive-th-control" onClick={() => toggleDriveCandidateSort('score')}>{section} <span>↕</span></button></th>)}
                        <th className="drive-score-col"><button type="button" className="drive-th-control" onClick={() => toggleDriveCandidateSort('score')}>Total <span>↕</span></button></th>
                        <th className="drive-status-th">
                          <button type="button" className="drive-status-trigger" onClick={() => setShowDriveStatusMenu(prev => !prev)}>
                            <span>Status</span>
                            <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                              <path d="M5 7.5 10 12.5 15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                          {showDriveStatusMenu && (
                            <div className="drive-status-menu">
                              {DRIVE_CANDIDATE_STATUS_OPTIONS.map(option => (
                                <button
                                  type="button"
                                  key={option}
                                  className={driveCandidateStatusFilter === option ? 'is-selected' : ''}
                                  onClick={() => { setDriveCandidateStatusFilter(option); setShowDriveStatusMenu(false); setDetailPage(1); }}
                                >
                                  {option}
                                </button>
                              ))}
                            </div>
                          )}
                        </th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedDetailCandidates.length === 0 ? (
                        <tr><td colSpan={5 + detailSections.length}><div className="drive-empty">No candidates added to this drive.</div></td></tr>
                      ) : paginatedDetailCandidates.map((candidate, index) => {
                        const candidateIsDefaulter = isDriveCandidateDefaulter(candidate);
                        const displayStatus = candidateIsDefaulter ? 'Defaulter' : (candidate.status || 'Added');
                        return (
                          <tr key={candidate.id}>
                            <td>{(detailPage - 1) * detailPageSize + index + 1}</td>
                            <td className="drive-candidate-name-cell">
                              <strong>{candidate.name}</strong>
                              <span>{candidate.email}</span>
                            </td>
                            {detailSections.map(section => {
                              const score = (candidate.section_scores || []).find(item => item.name === section);
                              return <td key={section}>{score?.score == null ? '-' : score.score}</td>;
                            })}
                            <td>{candidate.total_score == null ? '-' : `${Math.round(candidate.total_score)}%`}</td>
                            <td><span className={`drive-status-pill drive-status-pill--${String(displayStatus).toLowerCase().replace(/\s+/g, '-')}`}>{displayStatus}</span></td>
                            <td>
                              <div className="drive-row-actions">
                                <label className={`drive-select-box ${candidateIsDefaulter ? 'drive-select-box--disabled' : ''}`} title={candidateIsDefaulter ? 'Disqualified due to tab switching' : undefined}>
                                  <input type="checkbox" checked={selectedCandidateIds.has(candidate.id)} disabled={candidateIsDefaulter} onChange={() => toggleCandidateSelection(candidate.id)} />
                                  <span />
                                </label>
                                <button className="drive-eye-btn" onClick={() => handleOpenCandidateDetail(candidate)} title="View candidate">
                                  <svg width="17" height="17" viewBox="0 0 20 20" fill="none">
                                    <path d="M2.5 10s2.7-5 7.5-5 7.5 5 7.5 5-2.7 5-7.5 5-7.5-5-7.5-5Z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                                    <circle cx="10" cy="10" r="2.3" stroke="currentColor" strokeWidth="1.7"/>
                                  </svg>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="drive-detail-footer">
              <button
                type="button"
                className="drive-btn drive-btn--secondary drive-select-all-btn"
                disabled={filteredDetailCandidates.length === 0}
                onClick={handleSelectFilteredCandidates}
              >
                Select All
              </button>
              <button
                type="button"
                className="drive-btn drive-btn--danger"
                disabled={!canRejectSelected}
                onClick={handleRejectCandidates}
              >
                Reject
              </button>
              <button className="drive-btn drive-btn--primary" disabled={!canShortlistSelected} onClick={handleShortlistCandidates}>
                Shortlist
              </button>
            </div>

            {detailCandidates.length > 0 && (
              <div className="drive-pagination">
                <div className="drive-page-controls">
                  <button onClick={() => setDetailPage(1)} disabled={detailPage === 1}>{'<<'} Prev</button>
                  <button onClick={() => setDetailPage(page => Math.max(1, page - 1))} disabled={detailPage === 1}>{'<'} Prev</button>
                  {Array.from({ length: detailTotalPages }, (_, index) => index + 1).map(page => (
                    <button key={page} className={page === detailPage ? 'active' : ''} onClick={() => setDetailPage(page)}>{page}</button>
                  ))}
                  <button onClick={() => setDetailPage(page => Math.min(detailTotalPages, page + 1))} disabled={detailPage === detailTotalPages}>Next {'>'}</button>
                  <button onClick={() => setDetailPage(detailTotalPages)} disabled={detailPage === detailTotalPages}>Next {'>>'}</button>
                </div>
                <div className="drive-page-size">
                  <PrettySelect value={String(detailPageSize).padStart(2, '0')} options={['05', '10', '15']} onChange={value => { setDetailPageSize(Number(value)); setDetailPage(1); }} />
                  <span>Items per page</span>
                </div>
              </div>
            )}
          </>
        )}
      </main>

      {showCreate && (
        <CreateDriveModal
          exams={exams}
          mode={editingDrive ? 'edit' : 'create'}
          initialForm={editingDrive ? driveToForm(editingDrive) : (draftDriveForm || emptyDriveForm)}
          onClose={(closedForm) => {
            setShowCreate(false);
            if (!editingDrive && closedForm) setDraftDriveForm(closedForm);
            setEditingDrive(null);
          }}
          onCreate={handleCreate}
          onNotify={setToast}
        />
      )}
      {addingCandidatesDrive && !showAddChoose && !showAddBulkUpload && (
        <AddCandidateChoiceModal
          onClose={() => setAddingCandidatesDrive(null)}
          onSingleEntry={() => setShowAddChoose(true)}
          onBulkUpload={() => setShowAddBulkUpload(true)}
        />
      )}
      {showAddChoose && (
        <DriveChooseCandidatesModal
          onClose={() => { setShowAddChoose(false); setAddingCandidatesDrive(null); }}
          onAssign={attachExistingCandidatesToDrive}
        />
      )}
      {showAddBulkUpload && (
        <DriveBulkUploadForm
          onClose={() => { setShowAddBulkUpload(false); setAddingCandidatesDrive(null); }}
          onCreated={attachCreatedCandidatesToDrive}
          onNotify={setToast}
        />
      )}
      {candidateDetail && (
        <DriveCandidateDetailModal
          candidate={candidateDetail}
          loadingAnswers={candidateAnswersLoading}
          loadingScreenshots={candidateScreenshotsLoading}
          answers={candidateAnswers}
          screenshots={candidateScreenshots}
          onClose={() => setCandidateDetail(null)}
          onExport={handleExportCandidateCsv}
          onOpenScreenshot={handleOpenDriveScreenshot}
        />
      )}
      {driveToDelete && (
        <div className="em-confirm-overlay" onClick={() => !driveDeleting && setDriveToDelete(null)}>
          <div className="em-confirm-dialog" onClick={event => event.stopPropagation()}>
            <button className="em-confirm-close" onClick={() => setDriveToDelete(null)} disabled={driveDeleting}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M13 1L1 13M1 1L13 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <div className="em-confirm-icon">
              <svg width="33" height="33" viewBox="0 0 33 33" fill="none">
                <path d="M27.8238 6.18259H5.15258C4.87927 6.18259 4.61716 6.29116 4.4239 6.48442C4.23064 6.67767 4.12207 6.93979 4.12207 7.2131C4.12207 7.4864 4.23064 7.74852 4.4239 7.94178C4.61716 8.13504 4.87927 8.24361 5.15258 8.24361H6.18309V26.7928C6.18309 27.3394 6.40023 27.8636 6.78675 28.2501C7.17326 28.6367 7.69749 28.8538 8.24411 28.8538H24.7323C25.2789 28.8538 25.8031 28.6367 26.1896 28.2501C26.5761 27.8636 26.7933 27.3394 26.7933 26.7928V8.24361H27.8238C28.0971 8.24361 28.3592 8.13504 28.5525 7.94178C28.7457 7.74852 28.8543 7.4864 28.8543 7.2131C28.8543 6.93979 28.7457 6.67767 28.5525 6.48442C28.3592 6.29116 28.0971 6.18259 27.8238 6.18259ZM24.7323 26.7928H8.24411V8.24361H24.7323V26.7928ZM10.3051 3.09106C10.3051 2.81775 10.4137 2.55563 10.607 2.36238C10.8002 2.16912 11.0623 2.06055 11.3356 2.06055H21.6407C21.914 2.06055 22.1762 2.16912 22.3694 2.36238C22.5627 2.55563 22.6712 2.81775 22.6712 3.09106C22.6712 3.36437 22.5627 3.62648 22.3694 3.81974C22.1762 4.013 21.914 4.12157 21.6407 4.12157H11.3356C11.0623 4.12157 10.8002 4.013 10.607 3.81974C10.4137 3.62648 10.3051 3.36437 10.3051 3.09106Z" fill="#DC2626" />
              </svg>
            </div>
            <div className="em-confirm-copy">
              <h2>Delete this drive?</h2>
              <p>Are you sure you want to delete this drive?<br/>This action cannot be undone.</p>
              {driveDeleteError && <div className="em-confirm-error">{driveDeleteError}</div>}
            </div>
            <div className="em-confirm-actions">
              <button className="em-confirm-btn em-confirm-cancel" onClick={() => setDriveToDelete(null)} disabled={driveDeleting}>Cancel</button>
              <button className="em-confirm-btn em-confirm-delete" onClick={handleDeleteDriveConfirm} disabled={driveDeleting}>
                {driveDeleting ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}